import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { DEFAULT_TEXT_STYLE, TEXT_ZONES, TEXT_COLORS, SCRIM_COLORS, TEXT_STYLE_OPTIONS, TEXT_STYLES, zoneLayout, hexToRgba, scrimStyleFor } from '../lib/covers/text-styles';

const { FiType, FiRotateCcw, FiMessageCircle, FiArrowRight, FiLoader, FiAlertCircle } = FiIcons;

// Real backend — ms-studio's public demo-edit route (see /root/ms-studio/src/app/api/public/cover-demo-edit).
// No auth (it's a public marketing demo), narrowed to ONE fixed server-side image, rate-limited per IP,
// and (2026-08-20) gated behind Turnstile — widget "ManuscriptSmith Marketing — Cover Demo", scoped to
// manuscriptsmith.com / www / mkt-dev. Site key is public by design (Turnstile's model: the SECRET is
// what's confidential, verified server-side — never the site key).
const DEMO_EDIT_ENDPOINT = 'https://studio-dev.manuscriptsmith.com/studio/api/public/cover-demo-edit';
const DEFAULT_BG = '/img/cover-examples/demo-textfree.jpg';
const TURNSTILE_SITE_KEY = '0x4AAAAAAEXIYMOblg9Iku8_';

// Loads the Turnstile script once, shared across every CoverEditorDemo instance on the page
// (homepage + cover-studio + cover-showcase can all mount this component).
let turnstileScriptPromise = null;
function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (turnstileScriptPromise) return turnstileScriptPromise;
  turnstileScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return turnstileScriptPromise;
}

/**
 * Cover Editor Demo — the same direct-edit controls as the real, live Cover Studio backend
 * (ms-studio: placement / typography / colors / scrim, ported from kimballandvitaly.com/echo/
 * book-covers-editor.html), running here client-side on a real text-free CloviCover generation
 * so a visitor can try it before signing up. "Ask for a change" calls the SAME Gemini-backed
 * surgical-edit path the real app uses (via a narrowed, rate-limited public route) — a real
 * generation, not a mock.
 */
const CoverEditorDemo = ({ title = 'Try it live', bg = DEFAULT_BG }) => {
  const [ts, setTs] = useState(DEFAULT_TEXT_STYLE);
  const [prompt, setPrompt] = useState('');
  const [artUrl, setArtUrl] = useState(bg);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState(null);
  const [cfToken, setCfToken] = useState(null);
  const widgetElRef = useRef(null);
  const widgetIdRef = useRef(null);
  const update = (patch) => setTs((prev) => ({ ...prev, ...patch }));
  const reset = () => setTs(DEFAULT_TEXT_STYLE);

  // Mount the Turnstile widget once. Invisible/managed — Cloudflare decides whether the
  // visitor sees a checkbox or nothing at all; we just wait for the token callback.
  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled || !window.turnstile || !widgetElRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(widgetElRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        size: 'flexible',
        callback: (token) => setCfToken(token),
        'expired-callback': () => setCfToken(null),
        'error-callback': () => setCfToken(null),
      });
    }).catch(() => { /* verification unavailable — Apply stays disabled, no crash */ });
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) window.turnstile.remove(widgetIdRef.current);
    };
  }, []);

  const applyEdit = async () => {
    const instruction = prompt.trim();
    if (!instruction || editing || !cfToken) return;
    setEditing(true);
    setEditError(null);
    try {
      const res = await fetch(DEMO_EDIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction, cfToken }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Edit failed (${res.status})`);
      setArtUrl(data.image);
      setPrompt('');
    } catch (e) {
      setEditError(e.message || 'Edit failed — try again in a moment.');
    } finally {
      setEditing(false);
      setCfToken(null); // Turnstile tokens are single-use
      if (window.turnstile && widgetIdRef.current) window.turnstile.reset(widgetIdRef.current);
    }
  };

  const typo = TEXT_STYLES[ts.style];
  const layout = zoneLayout(ts.zone);
  const scrim = hexToRgba(ts.scrimColor, ts.scrimOpacity / 100);

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-6 items-start">
      {/* live preview */}
      <div className="mx-auto md:mx-0">
        <div className="relative w-[220px] rounded-lg overflow-hidden shadow-2xl ring-1 ring-black/10" style={{ aspectRatio: '3 / 4' }}>
          <div className="absolute inset-0 bg-cover bg-center transition-[background-image] duration-300" style={{ backgroundImage: `url(${artUrl})` }} />
          {editing && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <SafeIcon icon={FiLoader} className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
          <div className="absolute inset-0" style={scrimStyleFor(ts.zone, scrim)} />
          <div className="relative w-full h-full flex flex-col p-[9%]" style={{ justifyContent: layout.justify, alignItems: layout.align }}>
            <div className="flex flex-col gap-[6%]" style={{ textAlign: layout.textAlign, alignItems: layout.textAlign === 'left' ? 'flex-start' : 'center', maxWidth: ts.zone === 'left' ? '68%' : undefined }}>
              <div style={{
                color: ts.titleColor, fontFamily: typo.titleFont, fontWeight: typo.titleWeight,
                textTransform: typo.titleTransform === 'uppercase' ? 'uppercase' : undefined,
                letterSpacing: typo.titleLetterSpacing, lineHeight: 1.05,
                textShadow: '0 2px 10px rgba(0,0,0,0.5)', fontSize: 'clamp(16px,6.5vw,26px)',
              }}>{title}</div>
              <div style={{ color: ts.subtitleColor, fontFamily: typo.subtitleFont, fontStyle: typo.subtitleStyle, textShadow: '0 2px 8px rgba(0,0,0,0.5)', fontSize: 'clamp(9px,2.6vw,12px)' }}>a novel</div>
              <div style={{ color: ts.authorColor, fontFamily: typo.subtitleFont, textShadow: '0 2px 8px rgba(0,0,0,0.5)', fontSize: 'clamp(9px,2.6vw,12px)', letterSpacing: '1px' }}>Your Name</div>
            </div>
          </div>
        </div>
        <p className="text-center text-[11px] text-gray-400 mt-2">Real CloviCover art · text is live CSS, not baked in</p>
        {artUrl !== bg && (
          <button type="button" onClick={() => { setArtUrl(bg); setEditError(null); }} className="mx-auto mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-gray-400 hover:text-primary-600">
            <SafeIcon icon={FiRotateCcw} className="w-3 h-3" /> Reset art
          </button>
        )}
      </div>

      {/* controls — same library as the real Cover Studio app */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2 inline-flex items-center gap-1.5"><SafeIcon icon={FiType} className="w-3.5 h-3.5" /> Direct edit</div>
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-gray-200 bg-white p-4">
          <label className="text-[11px] font-semibold text-gray-600">Placement
            <select value={ts.zone} onChange={(e) => update({ zone: e.target.value })} className="mt-1 block w-full text-sm px-2 py-1.5 rounded-lg border border-gray-300 bg-white">
              {TEXT_ZONES.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-gray-600">Style
            <select value={ts.style} onChange={(e) => update({ style: e.target.value })} className="mt-1 block w-full text-sm px-2 py-1.5 rounded-lg border border-gray-300 bg-white">
              {TEXT_STYLE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-gray-600">Title color
            <select value={ts.titleColor} onChange={(e) => update({ titleColor: e.target.value })} className="mt-1 block w-full text-sm px-2 py-1.5 rounded-lg border border-gray-300 bg-white">
              {TEXT_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-gray-600">Author color
            <select value={ts.authorColor} onChange={(e) => update({ authorColor: e.target.value })} className="mt-1 block w-full text-sm px-2 py-1.5 rounded-lg border border-gray-300 bg-white">
              {TEXT_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-gray-600">Text background
            <select value={ts.scrimColor} onChange={(e) => update({ scrimColor: e.target.value })} className="mt-1 block w-full text-sm px-2 py-1.5 rounded-lg border border-gray-300 bg-white">
              {SCRIM_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-gray-600">Background opacity — {ts.scrimOpacity}%
            <input type="range" min={0} max={100} value={ts.scrimOpacity} onChange={(e) => update({ scrimOpacity: Number(e.target.value) })} className="mt-2.5 block w-full accent-primary-500" />
          </label>
          <button type="button" onClick={reset} className="sm:col-span-2 justify-self-start inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 hover:text-primary-600">
            <SafeIcon icon={FiRotateCcw} className="w-3 h-3" /> Reset text
          </button>
        </div>

        {/* AI edit — real call to the same Gemini surgical-edit path Cover Studio uses,
            via a public, rate-limited demo route (fixed base image, no auth). */}
        <div className="mt-4">
          <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2 inline-flex items-center gap-1.5"><SafeIcon icon={FiMessageCircle} className="w-3.5 h-3.5" /> Ask for a change</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={prompt} onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyEdit()}
              placeholder="make the background darker · replace the light with a lighthouse"
              maxLength={300}
              disabled={editing}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-primary-500 disabled:opacity-60" />
            <button type="button" onClick={applyEdit} disabled={editing || !prompt.trim() || !cfToken}
              title={!cfToken ? 'Verifying you\'re human — one moment' : undefined}
              className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50 whitespace-nowrap">
              {editing ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={FiArrowRight} className="w-4 h-4" />} {editing ? 'Editing…' : 'Apply'}
            </button>
          </div>
          {/* Turnstile mounts here — invisible/managed, so this is usually empty space */}
          <div ref={widgetElRef} className="mt-2" />
          {editError ? (
            <p className="text-[11px] text-red-500 mt-1.5 flex items-center gap-1"><SafeIcon icon={FiAlertCircle} className="w-3 h-3" /> {editError}</p>
          ) : (
            <p className="text-[11px] text-gray-400 mt-1.5">Real AI edit, live — a few tries here, unlimited inside Cover Studio on your own book.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoverEditorDemo;
